import { DefectSelection } from "../DefectSelection";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InspectorContext } from "../../reducer";
import { InspectorRow } from "@/api/hitachi/rbi";
import { MinMaxInput } from "@/components";
import { useAcceptRejectAll, useHandleFieldChanged } from "../../hooks";
import { useContext } from "react";
import { useInventory } from "../../queries";
import { useModal } from "@/hooks";

export function PT() {
  const { state, updateState } = useContext(InspectorContext);

  const cleaners = useInventory("PTCleaner");
  const penetrant = useInventory("PTPenetrant");
  const developers = useInventory("PTDeveloper");

  const modal = useModal();
  const acceptReject = useAcceptRejectAll();
  const handleFieldChanged = useHandleFieldChanged();
  const hasRepairs = state.inspections.some((x) => x.weldRepairWeld);

  function normalizeCopiedValue(field: keyof InspectorRow, value?: string) {
    if (!value) {
      return value;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value;
    }

    if (field === "luxLevel") {
      return `${Math.max(1000, numericValue)}`;
    }

    if (field === "surfaceTemp") {
      return `${Math.min(50, Math.max(10, numericValue))}`;
    }

    return value;
  }

  function handleCopy(field: keyof InspectorRow, value?: string) {
    const copiedValue = normalizeCopiedValue(field, value);

    updateState({
      type: "Inspections",
      payload: state.inspections.map((process) => {
        return { ...process, [field]: copiedValue };
      }),
    });
  }

  return (
    <>
      <thead>
        <tr>
          <th>Weld Map</th>
          <th>Drawing No.</th>
          <th>Weld ID No.</th>
          <th>Work Instruction</th>
          <th>Joint Type</th>
          <th>Indication Acceptance Level</th>
          <th>Defect Acceptance Level</th>
          <th>Lux Level</th>
          {state.inspections.length > 1 && <th />}
          <th>Surface Temp. (°C)</th>
          {state.inspections.length > 1 && <th />}
          <th>Cleaner Batch No.</th>
          {state.inspections.length > 1 && <th />}
          <th>Penetrant Batch No.</th>
          {state.inspections.length > 1 && <th />}
          <th>Developer Batch No.</th>
          {state.inspections.length > 1 && <th />}
          <th>Accept</th>
          <th>Reject</th>
          <th>Defects</th>
        </tr>
      </thead>
      <tbody>
        {state.inspections.map((row, index) => (
          <tr key={index}>
            <td>{row.weldMap}</td>
            <td>{row.drawingNumber}</td>
            <td>{row.weldNumber}</td>
            <td>{row.workInstruction}</td>
            <td>{row.jointType}</td>
            <td>{row.indicationAcceptanceLevel}</td>
            <td>{row.acceptanceLevel}</td>
            <td>
              <MinMaxInput
                min={1000}
                className="form-control"
                value={row.luxLevel ?? ""}
                onChange={(e) => handleFieldChanged(index, "luxLevel", e.currentTarget.value)}
              />
            </td>
            {state.inspections.length > 1 && (
              <td className="align-middle p-0">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("luxLevel", row.luxLevel)}
                  />
                )}
              </td>
            )}
            <td>
              <MinMaxInput
                min={10}
                max={50}
                className="form-control"
                value={row.surfaceTemp ?? ""}
                onChange={(e) => handleFieldChanged(index, "surfaceTemp", e.currentTarget.value)}
              />
            </td>
            {state.inspections.length > 1 && (
              <td className="align-middle p-0">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("surfaceTemp", row.surfaceTemp)}
                  />
                )}
              </td>
            )}
            <td>
              <select
                className="form-select"
                value={row.cleanerBatch ?? ""}
                onChange={(e) => handleFieldChanged(index, "cleanerBatch", e.currentTarget.value)}
              >
                <option />
                {row.weldRepairWeld && <option>N/A</option>}
                {cleaners.data?.map((cleaner) => (
                  <option key={cleaner.itemId}>{cleaner.batchNumber}</option>
                ))}
              </select>
            </td>
            {state.inspections.length > 1 && (
              <td className="align-middle p-0">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("cleanerBatch", row.cleanerBatch)}
                  />
                )}
              </td>
            )}
            <td>
              <select
                className="form-select"
                value={row.penetrantBatch ?? ""}
                onChange={(e) => handleFieldChanged(index, "penetrantBatch", e.currentTarget.value)}
              >
                <option />
                {row.weldRepairWeld && <option>N/A</option>}
                {penetrant.data?.map((penetrant) => (
                  <option key={penetrant.itemId}>{penetrant.batchNumber}</option>
                ))}
              </select>
            </td>
            {state.inspections.length > 1 && (
              <td className="align-middle p-0">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("penetrantBatch", row.penetrantBatch)}
                  />
                )}
              </td>
            )}
            <td>
              <select
                className="form-select"
                value={row.developerBatch ?? ""}
                onChange={(e) => handleFieldChanged(index, "developerBatch", e.currentTarget.value)}
              >
                <option />
                {row.weldRepairWeld && <option>N/A</option>}
                {developers.data?.map((developer) => (
                  <option key={developer.itemId}>{developer.batchNumber}</option>
                ))}
              </select>
            </td>
            {state.inspections.length > 1 && (
              <td className="align-middle p-0">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("developerBatch", row.developerBatch)}
                  />
                )}
              </td>
            )}
            <td>
              <input
                type="radio"
                className="form-check-input"
                name={`result${index}`}
                checked={row.accept === true}
                onChange={() => handleFieldChanged(index, "accept", true)}
                onClick={(e) => e.currentTarget.checked && handleFieldChanged(index, "accept", undefined)}
                disabled={
                  !row.luxLevel || !row.surfaceTemp || !row.cleanerBatch || !row.penetrantBatch || !row.developerBatch
                }
              />
            </td>
            <td>
              <input
                type="radio"
                className="form-check-input"
                name={`result${index}`}
                checked={row.accept === false}
                onChange={() => handleFieldChanged(index, "accept", false)}
                onClick={(e) => e.currentTarget.checked && handleFieldChanged(index, "accept", undefined)}
                disabled={
                  !row.luxLevel || !row.surfaceTemp || !row.cleanerBatch || !row.penetrantBatch || !row.developerBatch
                }
              />
            </td>
            <td>
              <button
                role="link"
                className="btn btn-link"
                disabled={row.accept !== false}
                onClick={() =>
                  modal.show(
                    <DefectSelection
                      caption="Defects"
                      weld={row}
                      onAccept={(defects) => handleFieldChanged(index, "defects", defects)}
                    />
                  )
                }
              >
                {row.defects?.length > 0 ? `${row.defects?.length} Selected` : "Click to select"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>

      {state.inspections.length > 1 && (
        <tfoot>
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            {hasRepairs && state.discipline !== "PMIPT" && <td />}
            <td />
            {state.inspections.length > 1 && <td />}
            <td />
            {state.inspections.length > 1 && <td />}
            <td />
            {state.inspections.length > 1 && <td />}
            <td />
            {state.inspections.length > 1 && <td />}
            <td />
            {state.inspections.length > 1 && <td />}
            <td>
              <button type="button" className="btn btn-default btn-sm" onClick={acceptReject.acceptAll}>
                Accept all
              </button>
            </td>
            <td>
              <button type="button" className="btn btn-default btn-sm" onClick={acceptReject.rejectAll}>
                Reject All
              </button>
            </td>
            <td />
          </tr>
        </tfoot>
      )}
    </>
  );
}
